import { useQuery } from '@tanstack/react-query';
import type {
  CitizenFileSummaryDto,
  CitizenFileTimelineDto,
  CitizenMilestoneType,
} from '@munifocus/contracts-gov';
import { api } from './api-client';

/** Every file at the municipality, as the public sees it. */
export function usePublicFiles(localGovernmentId: string | undefined) {
  return useQuery({
    queryKey: ['public-view', localGovernmentId],
    queryFn: async () =>
      (await api.get<CitizenFileSummaryDto[]>(`/public-view/${localGovernmentId}/files`)).data,
    enabled: Boolean(localGovernmentId),
  });
}

/** One file's public timeline. */
export function usePublicFile(localGovernmentId: string | undefined, fileId: string | undefined) {
  return useQuery({
    queryKey: ['public-view', localGovernmentId, fileId],
    queryFn: async () =>
      (await api.get<CitizenFileTimelineDto>(`/public-view/${localGovernmentId}/files/${fileId}`))
        .data,
    enabled: Boolean(localGovernmentId && fileId),
  });
}

/**
 * Milestone → label. The Client Portal's own keys, deliberately: this screen
 * shows a municipality exactly what the public sees, so it must say it in
 * exactly the same words. Exhaustive, so a new milestone cannot ship unlabelled.
 */
const LABEL_KEY: Record<CitizenMilestoneType, string> = {
  file_opened: 'public.milestone.fileOpened',
  site_visit_completed: 'public.milestone.siteVisitCompleted',
  notice_served: 'public.milestone.noticeServed',
  compliance_deadline: 'public.milestone.complianceDeadline',
  matter_referred: 'public.milestone.matterReferred',
  file_closed: 'public.milestone.fileClosed',
};

export function milestoneLabelKey(type: CitizenMilestoneType): string {
  return LABEL_KEY[type];
}
